import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    Injector,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { ITagModel } from '@fi-shared/models';
import { TagService } from '../tag.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ModuleType } from '@fi-shared/types/module-type';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { IItemsListAction2, ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { IModuleTypesItem } from '@fi-shared/models/module-types';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { SideListOptionType } from '@fi-shared/types';
import { TagListTableContainerComponent } from '@fi-app/tag/tag-list/tag-list-table-container/tag-list-table-container.component';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FiTagHelperService } from '../fi-tag-helper.service';

@Component({
    selector: 'fi-tag-list',
    templateUrl: './tag-list.component.html',
    styleUrls: ['./tag-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-tag-list fi-relative no-side-padding' },
    standalone: false,
})
export class TagListComponent extends FiRemoteStateFullDataSource<ITagModel> implements OnInit, AfterViewInit {
    public defaultSortColumnName = 'Name';
    public defaultSortDirection = false;
    public objectType = ObjectTypes.Tag;
    public itemsListType = FiItemsListType.TagList;

    defaultFilterType: SideListOptionType = SideListOptionType.All;

    @ViewChild(TagListTableContainerComponent) tableContainerComponent: TagListTableContainerComponent;

    private tagService = inject(TagService);
    private statisticService = inject(StatisticService);
    private stateAccessCheckService = inject(StateAccessCheckService);
    private fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);
    private fiTagHelperService = inject(FiTagHelperService);

    protected readonly tagAccessRight: boolean = this.stateAccessCheckService.isStateAllowedForCurrentUser(
        FiStateNames.Tag.Add,
    );
    protected readonly sideListOptionsAll: ISideListOptionsItem = this.fiTagHelperService.getSideListOptionAll();
    protected readonly sideListOptions: ISideListOptionsItem[] = this.fiTagHelperService.getSideListOptions();
    protected readonly itemsListActions: ReadonlyArray<IItemsListAction2> = this.fiTagHelperService.getItemsListActions(
        this.confirmDeleteSelectedTags.bind(this),
    );

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnInit(): void {
        this.statisticService.Visit(ModuleType.Tag);
    }

    public ngAfterViewInit(): void {
        // https://chainformation.atlassian.net/browse/FI-5412
        // this.matSort is undefined, we should replace it with proper one
        this.matSort = this.tableContainerComponent.matSort;
    }

    private confirmDeleteSelectedTags(): void {
        this.fiItemsDeleteHelperService
            .commonDeleteConfirm(ObjectTypes.Tag, this.selectedItems())
            .then(() => this.deleteSelectedTags())
            .catch(() => {});
    }

    private deleteSelectedTags(): Promise<void> {
        const selectedItems = this.selectedItems();
        const tagIdsToDelete = selectedItems.map((item) => item.Id);
        return this.tagService.DeleteTags(tagIdsToDelete).then(() => {
            this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                ObjectTypes.Tag,
                tagIdsToDelete,
                selectedItems[0].Name,
            );
            this.reloadData();
        });
    }

    public loadListDataFn(loadArgs: ILoadDataArgs) {
        const parameter = this.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };

        return this.tagService.getTagsListData(parameter);
    }
}
